/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// You can delete this file if you're not using it
const RECOVERY_RELOAD_KEY = '__vs_staticquery_recovery_reload__'
const RECOVERY_RELOAD_PARAM = '__vsr'
const GTM_CONTAINER_ID = process.env.GATSBY_GTM_ID || 'GTM-5XMQRJS'
const GTM_LOADED_FLAG = '__vs_gtm_loaded__'
const GTM_CONSENT_BOOTSTRAPPED_FLAG = '__vs_gtm_consent_bootstrapped__'

const shouldRecoverFromErrorText = (text = '') => {
  return (
    /Loading \(StaticQuery\)/i.test(text) ||
    /StaticQuery/i.test(text) ||
    /ChunkLoadError/i.test(text) ||
    /Loading chunk [\d]+ failed/i.test(text) ||
    /Failed to fetch/i.test(text)
  )
}

const shouldRecoverFromAssetPath = (assetPath = '') => {
  return (
    /\/page-data\//i.test(assetPath) ||
    /\/component---/i.test(assetPath) ||
    /\/app-.*\.js/i.test(assetPath) ||
    /\/framework-.*\.js/i.test(assetPath) ||
    /\/webpack-runtime-.*\.js/i.test(assetPath)
  )
}

const recoverWithOneTimeReload = (reason) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const now = Date.now()
    const url = new URL(window.location.href)
    const existingRaw = window.sessionStorage.getItem(RECOVERY_RELOAD_KEY)
    if (existingRaw) {
      return
    }

    if (url.searchParams.has(RECOVERY_RELOAD_PARAM)) {
      return
    }

    window.sessionStorage.setItem(
      RECOVERY_RELOAD_KEY,
      JSON.stringify({
        ts: now,
        reason,
      })
    )

    url.searchParams.set(RECOVERY_RELOAD_PARAM, String(now))
    window.location.replace(url.toString())
  } catch (error) {
    // Fall back to a normal reload if URL/session logic fails.
    window.location.reload()
  }
}

const installStaticQueryRecoveryGuards = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.addEventListener(
    'error',
    (event) => {
      const target = event.target
      const assetPath = target?.src || target?.href || ''
      if (shouldRecoverFromAssetPath(assetPath)) {
        recoverWithOneTimeReload(`asset-load-error:${assetPath}`)
        return
      }

      const message = event?.message || ''
      if (shouldRecoverFromErrorText(message)) {
        recoverWithOneTimeReload(`window-error:${message}`)
      }
    },
    true
  )

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason
    const reasonText = reason?.message || reason?.stack || String(reason || '')
    if (shouldRecoverFromErrorText(reasonText)) {
      recoverWithOneTimeReload(`unhandled-rejection:${reasonText}`)
    }
  })

  window.setTimeout(() => {
    const bodyText = window.document?.body?.innerText || ''
    if (shouldRecoverFromErrorText(bodyText)) {
      recoverWithOneTimeReload('staticquery-fallback-screen')
    }
  }, 5000)
}

const scheduleBackgroundCleanup = (task) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 3000 })
    return
  }

  window.setTimeout(task, 0)
}

const bootstrapGtmConsent = () => {
  if (typeof window === 'undefined' || window[GTM_CONSENT_BOOTSTRAPPED_FLAG]) {
    return
  }

  const gpc = navigator.globalPrivacyControl === true
  const adConsent = gpc ? 'denied' : 'granted'

  window.__vsGpc = gpc
  window.dataLayer = window.dataLayer || []

  // Must be a function declaration: Consent Mode reads the `arguments` object.
  function gtag() {
    window.dataLayer.push(arguments)
  }

  gtag('consent', 'default', {
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: adConsent,
    analytics_storage: 'granted',
  })

  window[GTM_CONSENT_BOOTSTRAPPED_FLAG] = true
}

const injectGtmScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  if (window[GTM_LOADED_FLAG] || !GTM_CONTAINER_ID) {
    return
  }

  bootstrapGtmConsent()

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
    platform: 'gatsby',
  })

  const gtmScript = document.createElement('script')
  gtmScript.async = true
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(gtmScript, firstScript)
  } else {
    document.head.appendChild(gtmScript)
  }

  window[GTM_LOADED_FLAG] = true
}

const installDeferredGtmLoader = () => {
  if (typeof window === 'undefined') {
    return
  }

  let hasLoaded = false
  const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll']

  const loadGtmOnce = () => {
    if (hasLoaded) {
      return
    }

    hasLoaded = true
    interactionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, loadGtmOnce)
    })
    injectGtmScript()
  }

  // Cookie banner state does not gate GTM. Idle-load so a silent visit still
  // gets the container; interaction only loads it sooner than the idle timeout.
  scheduleBackgroundCleanup(loadGtmOnce)
  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, loadGtmOnce, { passive: true, once: true })
  })
}

export function onClientEntry() {
  if (typeof window === 'undefined') {
    return
  }

  bootstrapGtmConsent()
  installStaticQueryRecoveryGuards()
  installDeferredGtmLoader()

  if (!('serviceWorker' in navigator)) {
    return
  }

  // Run stale service worker cleanup in the background to avoid blocking first render.
  scheduleBackgroundCleanup(async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))

      if ('caches' in window) {
        const cacheKeys = await caches.keys()
        await Promise.all(cacheKeys.map((key) => caches.delete(key)))
      }
    } catch (error) {
      // Do not block page render if unregistering a stale SW fails.
      console.warn('Unable to clean up service workers', error)
    }
  })
}

export function shouldUpdateScroll({ routerProps: { location }, getSavedScrollPosition }) {
  // Always scroll to the top on page changes
  window.scrollTo(0, 0)
  return false // Prevent Gatsby's default scroll restoration
}
